"use client";

import React, { ReactNode, createContext, useContext, useRef } from "react";

import { usePathname } from "next/navigation";

/**
 * Tracks the pathname the user was on immediately before the current one,
 * for in-app client-side (Link/router.push) transitions — `document.referrer`
 * doesn't update for those since no new document loads. Computed during
 * render (a plain ref comparison, not an effect) so there's no dependency on
 * effect-firing order relative to descendants: a parent always renders
 * before its children, so by the time any consumer below this provider
 * reads the value, it's already correct for the current navigation.
 *
 * Full-page navigations (plain `<a href>`, or arriving from a different app
 * like the legacy Gatsby site) aren't covered here — those should be
 * detected via `document.referrer` instead, which the browser sets
 * correctly on its own for same-origin full navigations.
 */
const PreviousPathnameContext = createContext<string | null>(null);

interface PreviousPathnameProviderProps {
  children: ReactNode;
}

const PreviousPathnameProvider = ({ children }: PreviousPathnameProviderProps) => {
  const pathname = usePathname();
  const currentPathnameRef = useRef<string | null>(null);
  const previousPathnameRef = useRef<string | null>(null);

  if (currentPathnameRef.current !== pathname) {
    previousPathnameRef.current = currentPathnameRef.current;
    currentPathnameRef.current = pathname;
  }

  return (
    <PreviousPathnameContext.Provider value={previousPathnameRef.current}>
      {children}
    </PreviousPathnameContext.Provider>
  );
};

const usePreviousPathname = () => useContext(PreviousPathnameContext);

export { PreviousPathnameProvider, usePreviousPathname };
